/*
 * Die freie Flaeche des HMI-Editors (docs/hmi-panel-ausbau.md §2): Bausteine
 * pixelgenau auf der Entwurfsflaeche eines Panels oder Handys.
 *
 * Gezeigt wird dasselbe wie in der Laufzeit (FreeStage): dieselben Klassen,
 * dieselbe Stapelfolge, dasselbe Theme. Darueber liegt eine zweite Ebene in
 * Bildschirmpixeln mit Auswahl, Griffen, Hilfslinien und Gummiband; sie
 * skaliert nicht mit, damit ein Griff bei jeder Zoomstufe gleich gross ist.
 *
 * GESTEN OHNE ZWISCHENSTAENDE IM PROJEKT. Waehrend eines Zuges lebt die Lage
 * nur hier (`draft`), erst beim Loslassen geht genau ein `store.apply` hinaus.
 * Das ist ein Rueckgaengig-Schritt je Geste, und es haelt das Ziehen
 * fluessig: das Projekt, die Befunde und das Eigenschaftsfeld rechnen nicht
 * bei jeder Mausbewegung neu. Die Inhalte der Bausteine sind memoisiert und
 * rendern beim Ziehen nicht neu, nur ihr Rahmen wandert.
 *
 * BEDIENBLIND. Die Inhalte bekommen keine Zeigerereignisse (ein Klick ist
 * hier eine Auswahl, kein Schaltbefehl), die Laufzeit steht auf
 * `editing: true`, und ein Knopf schreibt deshalb auch per Tastatur nicht.
 *
 * Mathematik: alles in Entwurfspixeln. Zeigerbewegungen werden durch den
 * Massstab der Buehne geteilt, bevor sie in freeModel.ts hineingehen.
 */

import type { ComponentChildren, JSX } from 'preact';
import { memo } from 'preact/compat';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';

import { HmiAssetPicker } from '../../components/HmiAssetPicker/HmiAssetPicker';
import { keepPanelInViewport } from '../HmiAppearanceMenu';
import { WidgetHost } from '../WidgetHost';
import { BARE_KINDS } from '../panel/FreeStage';
import { ThemeScope } from '../panel/ThemeScope';
import { useHmiRuntime } from '../panel/runtimeContext';
import { isHexColor } from '../themeModel';
import type { HmiProject, LayoutTarget, WidgetDefinition, WidgetKind, WidgetPlacement } from '../widgets';
import { WIDGET_KIND_DRAG_TYPE } from './dragTypes';
import {
  GRID_SPECS,
  LAYOUT_TARGET_ORDER,
  duplicateWidget,
  findScreen,
  freeCellSize,
  frameOfLayout,
  isFreeLayout,
  placementRect,
  removeWidget,
  setLayoutBackground,
  setPlacements,
  transferFreeLayout,
} from './editorModel';
import type { EditorStore } from './editorStore';
import {
  HANDLES,
  SNAP_GRID_PX,
  SNAP_THRESHOLD_SCREEN_PX,
  alignRects,
  boundsOf,
  clampRect,
  distributeRects,
  findFreeRect,
  matchRectSize,
  rectFromPoints,
  rectsIntersect,
  reorder,
  resizeRect,
  snapMove,
  snapResize,
  stackOrder,
  type FrameSize,
  type FreeAlign,
  type Guide,
  type Handle,
  type Rect,
  type ZOp,
} from './freeModel';
import { WIDGET_CATALOG } from './palette/widgetCatalog';
import '../hmiCell.css';
import '../panel/stage.css';
import './FreeCanvas.css';

interface Props {
  store: EditorStore;
  canEdit?: boolean;
  /** Ein Baustein aus der Palette wurde auf der Flaeche abgelegt; `rect` ist
   *  seine Lage in Entwurfspixeln. Angelegt wird er vom Aufrufer. */
  onDropWidget?: (kind: string, rect: Rect) => void;
}

/** Standardgroesse eines neuen Bausteins: die Rastergroesse aus dem Katalog,
 *  umgerechnet in Pixel dieses Ziels und auf das 8-px-Raster gerundet. */
export function defaultFreeSize(kind: string, target: LayoutTarget, frame: FrameSize): { w: number; h: number } {
  const entry = WIDGET_CATALOG[kind as WidgetKind];
  const cell = freeCellSize(target, frame);
  const round8 = (v: number) => Math.max(SNAP_GRID_PX * 4, Math.round(v / SNAP_GRID_PX) * SNAP_GRID_PX);
  return {
    w: Math.min(frame.width, round8((entry?.colSpan ?? 3) * cell.w)),
    h: Math.min(frame.height, round8((entry?.rowSpan ?? 2) * cell.h)),
  };
}

/** Freier Platz fuer einen neuen Baustein auf einer freien Anordnung. */
export function freeSlotFor(project: HmiProject, screenId: string, target: LayoutTarget, kind: string): Rect {
  const layout = findScreen(project, screenId)?.layouts.find((l) => l.target === target);
  const frame = layout ? frameOfLayout(layout) : { width: 1280, height: 800 };
  const taken = Object.values(layout?.placements ?? {})
    .map(placementRect)
    .filter((r): r is Rect => r !== null);
  return findFreeRect(taken, defaultFreeSize(kind, target, frame), frame);
}

type Zoom = 'fit' | number;

type Gesture =
  | {
      kind: 'move';
      pointerId: number;
      startX: number;
      startY: number;
      ids: string[];
      start: Record<string, Rect>;
      moved: boolean;
      /** Klick auf ein schon gewaehltes: erst beim Loslassen ohne Zug wird es
       *  die alleinige Auswahl, sonst zieht der Zug die ganze Gruppe. */
      collapseTo: string | null;
    }
  | {
      kind: 'resize';
      pointerId: number;
      startX: number;
      startY: number;
      id: string;
      handle: Handle;
      start: Rect;
      moved: boolean;
    }
  | {
      kind: 'marquee';
      pointerId: number;
      origin: { x: number; y: number };
      base: readonly string[];
      moved: boolean;
    };

const DRAG_START_PX = 3;
const VIEWPORT_PAD = 28;

export function FreeCanvas({ store, canEdit = true, onDropWidget }: Props) {
  const project = store.project.value;
  const screenId = store.activeScreenId.value;
  const target = store.activeTarget.value;
  const selection = store.selection.value;
  const runtime = useHmiRuntime();

  const spec = GRID_SPECS[target];
  const screen = findScreen(project, screenId);
  const layout = screen?.layouts.find((l) => l.target === target) ?? null;
  const frame = layout ? frameOfLayout(layout) : { width: 1280, height: 800 };
  const widgetIds = screen?.widgetIds ?? [];
  const widgetsById = useMemo(() => new Map(project.widgets.map((w) => [w.id, w] as const)), [project.widgets]);

  // ------------------------------------------------ Lage aller Bausteine
  //
  // Was im Projekt steht, ergaenzt um eine vorlaeufige Lage fuer Bausteine,
  // die auf diesem Ziel noch keine haben (auf einem anderen Ziel angelegt).
  // Wie im Raster: gestrichelt zeigen, festschreiben beim ersten Anfassen.
  const { placements, unplaced } = useMemo(() => {
    const out: Record<string, WidgetPlacement> = {};
    const missing: string[] = [];
    const stored = layout?.placements ?? {};
    for (const id of widgetIds) {
      const p = stored[id];
      if (p && placementRect(p)) out[id] = p;
      else missing.push(id);
    }
    let z = Object.values(out).reduce((m, p) => Math.max(m, p.z ?? 0), -1) + 1;
    for (const id of missing) {
      const taken = Object.values(out).map(placementRect).filter((r): r is Rect => r !== null);
      const kind = widgetsById.get(id)?.kind ?? 'value-tile';
      const r = findFreeRect(taken, defaultFreeSize(kind, target, frame), frame);
      out[id] = { col: 0, row: 0, colSpan: 1, rowSpan: 1, ...r, z };
      z += 1;
    }
    return { placements: out, unplaced: missing };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, widgetIds, widgetsById, target, frame.width, frame.height]);

  const rects = useMemo(() => {
    const out: Record<string, Rect> = {};
    for (const [id, p] of Object.entries(placements)) out[id] = placementRect(p)!;
    return out;
  }, [placements]);

  const order = useMemo(
    () => stackOrder(widgetIds.filter((id) => placements[id]), (id) => placements[id]?.z),
    [widgetIds, placements],
  );

  const [draft, setDraftState] = useState<Record<string, Rect> | null>(null);
  // Der Entwurf auch als Ref: das Loslassen kann vor dem naechsten Rendern
  // kommen und muss den letzten Stand festschreiben, nicht den gerenderten.
  const draftRef = useRef<Record<string, Rect> | null>(null);
  const setDraft = (d: Record<string, Rect> | null) => { draftRef.current = d; setDraftState(d); };
  const [guides, setGuides] = useState<Guide[]>([]);
  const [marquee, setMarquee] = useState<Rect | null>(null);
  const shown = draft ? { ...rects, ...draft } : rects;

  const [snapOn, setSnapOn] = useState(true);
  const [gridOn, setGridOn] = useState(true);
  const [zoom, setZoom] = useState<Zoom>('fit');

  // --------------------------------------------------------- Massstab
  const viewportRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fitScale = box.w === 0
    ? 0
    : Math.max(0.05, Math.min((box.w - VIEWPORT_PAD * 2) / frame.width, (box.h - VIEWPORT_PAD * 2) / frame.height));
  const scale = zoom === 'fit' ? fitScale : zoom;

  // Der jeweils aktuelle Stand fuer Rueckrufe, die laenger leben als ein Rendern.
  const live = useRef({ rects, placements, order, scale, frame, snapOn, gridOn, widgetIds, unplaced });
  live.current = { rects, placements, order, scale, frame, snapOn, gridOn, widgetIds, unplaced };
  const gesture = useRef<Gesture | null>(null);

  // ------------------------------------------------------- Festschreiben

  /** Neue Lagen (und Ebenen) ins Projekt, samt aller vorlaeufigen. */
  function commit(next: Record<string, Partial<WidgetPlacement>>, coalesceKey?: string): void {
    const cur = live.current;
    const all: Record<string, WidgetPlacement> = {};
    for (const id of cur.widgetIds) {
      const p = cur.placements[id];
      if (!p) continue;
      all[id] = { ...p, ...(next[id] ?? {}) };
    }
    store.apply(setPlacements(store.project.peek(), screenId, target, all), coalesceKey);
  }

  function commitRects(next: Record<string, Rect>, coalesceKey?: string): void {
    const patch: Record<string, Partial<WidgetPlacement>> = {};
    for (const [id, r] of Object.entries(next)) patch[id] = { x: r.x, y: r.y, w: r.w, h: r.h };
    commit(patch, coalesceKey);
  }

  const isLocked = (id: string) => live.current.placements[id]?.locked === true;

  // ------------------------------------------------------------ Zeiger

  function toDesign(clientX: number, clientY: number): { x: number; y: number } {
    const el = surfaceRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    const s = live.current.scale || 1;
    return { x: (clientX - r.left) / s, y: (clientY - r.top) / s };
  }

  function snapOptions(alt: boolean) {
    const cur = live.current;
    return {
      threshold: SNAP_THRESHOLD_SCREEN_PX / (cur.scale || 1),
      grid: cur.snapOn && !alt ? SNAP_GRID_PX : 0,
      objects: cur.snapOn && !alt,
    };
  }

  function onItemPointerDown(ev: PointerEvent, id: string): void {
    if (ev.button !== 0) return;
    ev.stopPropagation();
    ev.preventDefault();
    viewportRef.current?.focus({ preventScroll: true });
    const current = store.selection.peek();
    const additive = ev.shiftKey || ev.ctrlKey || ev.metaKey;
    let ids: readonly string[];
    let collapseTo: string | null = null;
    if (additive) {
      if (current.includes(id)) {
        store.selection.value = current.filter((x) => x !== id);
        return;
      }
      ids = [...current, id];
      store.selection.value = ids;
    } else if (current.includes(id)) {
      ids = current;
      if (current.length > 1) collapseTo = id;
    } else {
      ids = [id];
      store.selection.value = ids;
    }
    if (!canEdit) return;
    const movable = ids.filter((x) => !isLocked(x) && live.current.rects[x]);
    if (movable.length === 0) return;
    const start: Record<string, Rect> = {};
    for (const x of movable) start[x] = live.current.rects[x];
    gesture.current = {
      kind: 'move', pointerId: ev.pointerId, startX: ev.clientX, startY: ev.clientY,
      ids: movable, start, moved: false, collapseTo,
    };
    attach();
  }

  function onHandlePointerDown(ev: PointerEvent, id: string, handle: Handle): void {
    if (ev.button !== 0 || !canEdit) return;
    ev.stopPropagation();
    ev.preventDefault();
    const start = live.current.rects[id];
    if (!start) return;
    gesture.current = {
      kind: 'resize', pointerId: ev.pointerId, startX: ev.clientX, startY: ev.clientY,
      id, handle, start, moved: false,
    };
    attach();
  }

  function onSurfacePointerDown(ev: PointerEvent): void {
    if (ev.button !== 0) return;
    viewportRef.current?.focus({ preventScroll: true });
    const additive = ev.shiftKey || ev.ctrlKey || ev.metaKey;
    gesture.current = {
      kind: 'marquee', pointerId: ev.pointerId, origin: toDesign(ev.clientX, ev.clientY),
      base: additive ? store.selection.peek() : [], moved: false,
    };
    attach();
  }

  // Die Fensterhoerer einer Geste stammen aus dem Rendern, in dem sie begann;
  // abgemeldet wird genau dieses Trio, auch beim Abbau der Flaeche.
  const listeners = useRef<{ move(ev: PointerEvent): void; up(ev: PointerEvent): void; cancel(ev: PointerEvent): void } | null>(null);
  function attach(): void {
    detach();
    const l = { move: onPointerMove, up: onPointerUp, cancel: onPointerCancel };
    listeners.current = l;
    window.addEventListener('pointermove', l.move);
    window.addEventListener('pointerup', l.up);
    window.addEventListener('pointercancel', l.cancel);
  }
  function detach(): void {
    const l = listeners.current;
    if (!l) return;
    listeners.current = null;
    window.removeEventListener('pointermove', l.move);
    window.removeEventListener('pointerup', l.up);
    window.removeEventListener('pointercancel', l.cancel);
  }
  useEffect(() => () => detach(), []);

  function onPointerMove(ev: PointerEvent): void {
    const g = gesture.current;
    if (!g || ev.pointerId !== g.pointerId) return;
    const cur = live.current;
    const s = cur.scale || 1;

    if (g.kind === 'marquee') {
      const p = toDesign(ev.clientX, ev.clientY);
      if (!g.moved && Math.hypot(p.x - g.origin.x, p.y - g.origin.y) * s < DRAG_START_PX) return;
      g.moved = true;
      const r = rectFromPoints(g.origin, p);
      setMarquee(r);
      const hit = cur.order.filter((id) => cur.rects[id] && rectsIntersect(r, cur.rects[id]));
      const next = [...g.base, ...hit.filter((id) => !g.base.includes(id))];
      const prev = store.selection.peek();
      if (next.length !== prev.length || next.some((id, i) => id !== prev[i])) store.selection.value = next;
      return;
    }

    let dx = (ev.clientX - g.startX) / s;
    let dy = (ev.clientY - g.startY) / s;
    if (!g.moved && Math.hypot(dx, dy) * s < DRAG_START_PX) return;
    g.moved = true;

    if (g.kind === 'move') {
      if (ev.shiftKey) {
        if (Math.abs(dx) > Math.abs(dy)) dy = 0;
        else dx = 0;
      }
      const bounds = boundsOf(Object.values(g.start))!;
      const others = cur.order.filter((id) => !g.ids.includes(id)).map((id) => cur.rects[id]);
      const moved: Rect = { ...bounds, x: bounds.x + dx, y: bounds.y + dy };
      const snapped = snapMove(moved, others, cur.frame, snapOptions(ev.altKey));
      let ddx = (ev.shiftKey && dx === 0) ? 0 : snapped.rect.x - bounds.x;
      let ddy = (ev.shiftKey && dy === 0) ? 0 : snapped.rect.y - bounds.y;
      ddx = Math.max(-bounds.x, Math.min(cur.frame.width - bounds.x - bounds.w, ddx));
      ddy = Math.max(-bounds.y, Math.min(cur.frame.height - bounds.y - bounds.h, ddy));
      const next: Record<string, Rect> = {};
      for (const [id, r] of Object.entries(g.start)) {
        next[id] = { ...r, x: Math.round(r.x + ddx), y: Math.round(r.y + ddy) };
      }
      setDraft(next);
      setGuides(snapped.guides);
      return;
    }

    // resize
    const keepAspect = ev.shiftKey;
    const raw = resizeRect(g.start, g.handle, dx, dy, { keepAspect });
    const others = cur.order.filter((id) => id !== g.id).map((id) => cur.rects[id]);
    const snapped = keepAspect
      ? { rect: raw, guides: [] as Guide[] }
      : snapResize(raw, g.handle, others, cur.frame, snapOptions(ev.altKey));
    setDraft({ [g.id]: cutToFrame(snapped.rect, cur.frame) });
    setGuides(snapped.guides);
  }

  function onPointerUp(ev: PointerEvent): void {
    const g = gesture.current;
    if (!g || ev.pointerId !== g.pointerId) return;
    gesture.current = null;
    detach();
    setGuides([]);
    setMarquee(null);

    if (g.kind === 'marquee') {
      if (!g.moved && g.base.length === 0 && store.selection.peek().length > 0) store.selection.value = [];
      return;
    }
    if (!g.moved) {
      setDraft(null);
      if (g.kind === 'move' && g.collapseTo) store.selection.value = [g.collapseTo];
      return;
    }
    const last = draftRef.current;
    setDraft(null);
    if (last) commitRects(last);
  }

  function onPointerCancel(ev: PointerEvent): void {
    const g = gesture.current;
    if (!g || ev.pointerId !== g.pointerId) return;
    gesture.current = null;
    detach();
    setDraft(null);
    setGuides([]);
    setMarquee(null);
  }

  // ---------------------------------------------------------- Aktionen

  function selectedMovable(): string[] {
    return store.selection.peek().filter((id) => live.current.rects[id] && !isLocked(id));
  }

  function onAlign(mode: FreeAlign): void {
    const ids = store.selection.peek().filter((id) => live.current.rects[id]);
    const out = alignRects(live.current.rects, ids, mode, live.current.frame);
    commitRects(onlyMovable(out, ids));
  }
  function onDistribute(axis: 'horizontal' | 'vertical'): void {
    const ids = store.selection.peek().filter((id) => live.current.rects[id]);
    commitRects(onlyMovable(distributeRects(live.current.rects, ids, axis), ids));
  }
  function onMatch(dimension: 'width' | 'height'): void {
    const ids = store.selection.peek().filter((id) => live.current.rects[id]);
    const out = matchRectSize(live.current.rects, ids, dimension);
    const clamped: Record<string, Rect> = {};
    for (const [id, r] of Object.entries(onlyMovable(out, ids))) clamped[id] = clampRect(r, live.current.frame);
    commitRects(clamped);
  }
  /** Gesperrte bleiben stehen, auch wenn sie in der Auswahl sind. */
  function onlyMovable(out: Record<string, Rect>, ids: readonly string[]): Record<string, Rect> {
    const keep: Record<string, Rect> = {};
    for (const id of ids) if (!isLocked(id) && out[id]) keep[id] = out[id];
    return keep;
  }

  function onZ(op: ZOp): void {
    const ids = store.selection.peek();
    if (ids.length === 0) return;
    const next = reorder(live.current.order, ids, op);
    const patch: Record<string, Partial<WidgetPlacement>> = {};
    next.forEach((id, z) => { patch[id] = { z }; });
    commit(patch);
  }

  function onToggleLock(): void {
    const ids = store.selection.peek().filter((id) => live.current.placements[id]);
    if (ids.length === 0) return;
    const allLocked = ids.every(isLocked);
    const patch: Record<string, Partial<WidgetPlacement>> = {};
    for (const id of ids) patch[id] = { locked: allLocked ? undefined : true };
    commit(patch);
  }

  function onDuplicate(): void {
    const ids = store.selection.peek();
    if (ids.length === 0) return;
    let next = materialise(store.project.peek());
    const before = new Set(next.widgets.map((w) => w.id));
    for (const id of ids) next = duplicateWidget(next, screenId, target, id);
    store.apply(next);
    store.selection.value = next.widgets.map((w) => w.id).filter((id) => !before.has(id));
  }

  function onDelete(): void {
    const ids = store.selection.peek();
    if (ids.length === 0) return;
    store.apply(ids.reduce((acc, id) => removeWidget(acc, id), store.project.peek()));
  }

  /** Vorlaeufige Lagen festschreiben, bevor eine Aktion das Projekt aendert. */
  function materialise(current: HmiProject): HmiProject {
    if (live.current.unplaced.length === 0) return current;
    return setPlacements(current, screenId, target, live.current.placements);
  }

  function onNudge(dx: number, dy: number): void {
    const ids = selectedMovable();
    if (ids.length === 0) return;
    const cur = live.current;
    const bounds = boundsOf(ids.map((id) => cur.rects[id]))!;
    const ddx = Math.max(-bounds.x, Math.min(cur.frame.width - bounds.x - bounds.w, dx));
    const ddy = Math.max(-bounds.y, Math.min(cur.frame.height - bounds.y - bounds.h, dy));
    if (ddx === 0 && ddy === 0) return;
    const next: Record<string, Rect> = {};
    for (const id of ids) next[id] = { ...cur.rects[id], x: cur.rects[id].x + ddx, y: cur.rects[id].y + ddy };
    commitRects(next, `frei-tastatur:${screenId}:${target}:${ids.join(',')}`);
  }

  function onGeometry(id: string, field: keyof Rect, value: number): void {
    const r = live.current.rects[id];
    if (!r || !Number.isFinite(value)) return;
    commitRects({ [id]: clampRect({ ...r, [field]: value }, live.current.frame) }, `frei-feld:${id}:${field}`);
  }

  // ---------------------------------------------------------- Tastatur
  useEffect(() => {
    function onKeyDown(ev: KeyboardEvent): void {
      if (isTextEntry(document.activeElement)) return;
      if (document.querySelector('[role="dialog"]')) return;
      const mod = ev.ctrlKey || ev.metaKey;
      const key = ev.key;

      if (mod && (key === 'z' || key === 'Z') && !ev.shiftKey) { ev.preventDefault(); store.undo(); return; }
      if (mod && (key === 'y' || key === 'Y' || ((key === 'z' || key === 'Z') && ev.shiftKey))) {
        ev.preventDefault(); store.redo(); return;
      }
      if (mod && (key === 'a' || key === 'A')) {
        ev.preventDefault();
        store.selection.value = [...live.current.order];
        return;
      }
      if (key === 'Escape') { store.selection.value = []; return; }
      if (store.selection.peek().length === 0 || !canEdit) return;

      if (key === 'Delete' || key === 'Backspace') { ev.preventDefault(); onDelete(); return; }
      if (mod && (key === 'd' || key === 'D')) { ev.preventDefault(); onDuplicate(); return; }
      if (mod && (key === ']' || key === '}')) { ev.preventDefault(); onZ(ev.shiftKey ? 'front' : 'forward'); return; }
      if (mod && (key === '[' || key === '{')) { ev.preventDefault(); onZ(ev.shiftKey ? 'back' : 'backward'); return; }

      const step = ev.shiftKey ? 10 : 1;
      const arrows: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
      };
      const a = arrows[key];
      if (a) { ev.preventDefault(); onNudge(a[0], a[1]); }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenId, target, canEdit]);

  // ------------------------------------------------------------ Ablegen
  const [dropHover, setDropHover] = useState(false);
  const carriesKind = (ev: DragEvent) => Array.from(ev.dataTransfer?.types ?? []).includes(WIDGET_KIND_DRAG_TYPE);
  function onDragOver(ev: DragEvent): void {
    if (!onDropWidget || !canEdit || !carriesKind(ev)) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    setDropHover(true);
  }
  function onDrop(ev: DragEvent): void {
    setDropHover(false);
    if (!onDropWidget || !canEdit || !carriesKind(ev)) return;
    ev.preventDefault();
    const kind = ev.dataTransfer?.getData(WIDGET_KIND_DRAG_TYPE) ?? '';
    if (!kind) return;
    const p = toDesign(ev.clientX, ev.clientY);
    const size = defaultFreeSize(kind, target, frame);
    const snap = (v: number) => (snapOn ? Math.round(v / SNAP_GRID_PX) * SNAP_GRID_PX : Math.round(v));
    onDropWidget(kind, clampRect({ x: snap(p.x - size.w / 2), y: snap(p.y - size.h / 2), ...size }, frame));
  }

  // -------------------------------------------------------- Uebernehmen
  const sources = LAYOUT_TARGET_ORDER.filter((t) => t !== target
    && (screen?.layouts.find((l) => l.target === t && Object.keys(l.placements).length > 0)));
  const [transferFrom, setTransferFrom] = useState<LayoutTarget | ''>('');
  useEffect(() => setTransferFrom(''), [target, screenId]);
  function onTransfer(): void {
    const from = transferFrom || sources[0];
    if (!from) return;
    const placed = transferFreeLayout(store.project.peek(), screenId, from, target);
    if (Object.keys(placed).length === 0) return;
    commit(placed);
  }

  // ------------------------------------------------------- Darstellung
  const sel = selection.filter((id) => shown[id]);
  const single = sel.length === 1 ? sel[0] : null;
  const multi = sel.length > 1;
  const anyLocked = sel.some(isLocked);
  const allLocked = sel.length > 0 && sel.every(isLocked);
  const groupBounds = multi ? boundsOf(sel.map((id) => shown[id])) : null;
  const background = layout?.background;
  const g = gesture.current;
  const badge = draft && g && g.kind !== 'marquee'
    ? (() => {
      const b = boundsOf(Object.values(draft))!;
      return { rect: b, text: g.kind === 'resize' ? `${b.w} × ${b.h}` : `${b.x}, ${b.y}` };
    })()
    : null;

  const surfaceStyle: Record<string, string> = {
    width: `${frame.width}px`,
    height: `${frame.height}px`,
    transform: `scale(${scale})`,
  };
  if (isHexColor(background?.color)) surfaceStyle.backgroundColor = background!.color!;
  if (background?.assetRef) {
    surfaceStyle.backgroundImage = `url("${runtime.assetUrl(background.assetRef).replace(/["\\]/g, '')}")`;
  }
  if (gridOn) {
    surfaceStyle['--fc-grid'] = `${SNAP_GRID_PX * 4}px`;
  }

  if (!screen || !layout || !isFreeLayout(layout)) return null;

  return (
    <div class="hv-fc">
      <div class="hv-fc__bar">
        <span class="hv-fc__target">{spec.label}</span>
        <span class="hv-fc__measure">
          Frei, {frame.width} × {frame.height} px
          {scale > 0 && <span class="hv-fc__zoomText"> · {Math.round(scale * 100)} %</span>}
        </span>
        <span class="hv-fc__spacer" />
        <label class="hv-fc__check" title="An Raster, Kanten und Mitten einrasten. Alt beim Ziehen schaltet es kurz ab.">
          <input type="checkbox" checked={snapOn} onChange={(e) => setSnapOn((e.currentTarget as HTMLInputElement).checked)} />
          Einrasten
        </label>
        <label class="hv-fc__check" title="Hilfsraster auf der Fläche zeigen (wird nicht gespeichert)">
          <input type="checkbox" checked={gridOn} onChange={(e) => setGridOn((e.currentTarget as HTMLInputElement).checked)} />
          Raster zeigen
        </label>
        <select
          class="hv-fc__select"
          aria-label="Zoom"
          value={String(zoom)}
          onChange={(e) => {
            const v = (e.currentTarget as HTMLSelectElement).value;
            setZoom(v === 'fit' ? 'fit' : Number(v));
          }}
        >
          <option value="fit">Einpassen</option>
          <option value="0.5">50 %</option>
          <option value="0.75">75 %</option>
          <option value="1">100 %</option>
        </select>
      </div>

      {canEdit && (
        <div class="hv-fc__tools" role="toolbar" aria-label="Werkzeuge der freien Anordnung">
          <ToolGroup label="Ausrichten">
            <Tool icon="alignLeft" title={multi ? 'Linke Kanten' : 'Links an den Rand'} disabled={sel.length === 0} onClick={() => onAlign('left')} />
            <Tool icon="alignHCenter" title={multi ? 'Waagrecht mittig' : 'Waagrecht in die Mitte'} disabled={sel.length === 0} onClick={() => onAlign('hcenter')} />
            <Tool icon="alignRight" title={multi ? 'Rechte Kanten' : 'Rechts an den Rand'} disabled={sel.length === 0} onClick={() => onAlign('right')} />
            <Tool icon="alignTop" title={multi ? 'Obere Kanten' : 'Oben an den Rand'} disabled={sel.length === 0} onClick={() => onAlign('top')} />
            <Tool icon="alignVCenter" title={multi ? 'Senkrecht mittig' : 'Senkrecht in die Mitte'} disabled={sel.length === 0} onClick={() => onAlign('vcenter')} />
            <Tool icon="alignBottom" title={multi ? 'Untere Kanten' : 'Unten an den Rand'} disabled={sel.length === 0} onClick={() => onAlign('bottom')} />
          </ToolGroup>
          <ToolGroup label="Verteilen">
            <Tool icon="distH" title="Waagrecht gleiche Abstände (ab drei)" disabled={sel.length < 3} onClick={() => onDistribute('horizontal')} />
            <Tool icon="distV" title="Senkrecht gleiche Abstände (ab drei)" disabled={sel.length < 3} onClick={() => onDistribute('vertical')} />
            <Tool icon="sameW" title="Gleich breit wie das zuerst gewählte" disabled={!multi} onClick={() => onMatch('width')} />
            <Tool icon="sameH" title="Gleich hoch wie das zuerst gewählte" disabled={!multi} onClick={() => onMatch('height')} />
          </ToolGroup>
          <ToolGroup label="Ebene">
            <Tool icon="front" title="Ganz nach vorne (Strg+Umschalt+])" disabled={sel.length === 0} onClick={() => onZ('front')} />
            <Tool icon="forward" title="Nach vorne (Strg+])" disabled={sel.length === 0} onClick={() => onZ('forward')} />
            <Tool icon="backward" title="Nach hinten (Strg+[)" disabled={sel.length === 0} onClick={() => onZ('backward')} />
            <Tool icon="back" title="Ganz nach hinten (Strg+Umschalt+[)" disabled={sel.length === 0} onClick={() => onZ('back')} />
          </ToolGroup>
          <ToolGroup label="Auswahl">
            <Tool icon={allLocked ? 'unlock' : 'lock'} title={allLocked ? 'Entsperren' : 'Sperren: nicht mehr verschiebbar'}
                  pressed={anyLocked} disabled={sel.length === 0} onClick={onToggleLock} />
            <Tool icon="duplicate" title="Duplizieren (Strg+D)" disabled={sel.length === 0} onClick={onDuplicate} />
            <Tool icon="trash" title="Löschen (Entf)" disabled={sel.length === 0} onClick={onDelete} />
          </ToolGroup>
          <span class="hv-fc__spacer" />
          <BackgroundMenu
            color={background?.color}
            assetRef={background?.assetRef}
            onChange={(next, key) => store.apply(setLayoutBackground(store.project.peek(), screenId, target, next), key)}
          />
          {sources.length > 0 && (
            <span class="hv-fc__transfer">
              <select
                class="hv-fc__select"
                aria-label="Anordnung übernehmen von"
                value={transferFrom || sources[0]}
                onChange={(e) => setTransferFrom((e.currentTarget as HTMLSelectElement).value as LayoutTarget)}
              >
                {sources.map((t) => <option key={t} value={t}>von {GRID_SPECS[t].label}</option>)}
              </select>
              <button type="button" class="hv-btn" onClick={onTransfer}
                      title="Die Anordnung des gewählten Ziels gleichmäßig auf diese Fläche umrechnen. Rückgängig nimmt es zurück.">
                Übernehmen
              </button>
            </span>
          )}
        </div>
      )}

      {unplaced.length > 0 && (
        <p class="hv-fc__note">
          {unplaced.length === 1 ? 'Ein Baustein hat' : `${unplaced.length} Bausteine haben`} auf {spec.label} noch
          keinen festen Platz. Sie stehen gestrichelt an einer freien Stelle und werden gespeichert, sobald du
          hier etwas veränderst.
        </p>
      )}

      <div
        ref={viewportRef}
        tabIndex={-1}
        class={`hv-fc__viewport${dropHover ? ' hv-fc__viewport--drop' : ''}${zoom === 'fit' ? ' hv-fc__viewport--fit' : ''}`}
        onPointerDown={(ev) => { if (ev.target === ev.currentTarget) onSurfacePointerDown(ev); }}
        onDragOver={onDragOver}
        onDragLeave={() => setDropHover(false)}
        onDrop={onDrop}
      >
        {scale > 0 && (
          <div class="hv-fc__stage" style={{ width: `${frame.width * scale}px`, height: `${frame.height * scale}px` }}>
            <ThemeScope appearance={project.appearance} class="hv-fc__theme">
              <div
                ref={surfaceRef}
                class={`hv-stage__surface hv-fc__surface${gridOn ? ' hv-fc__surface--grid' : ''}`}
                style={surfaceStyle}
                onPointerDown={(ev) => { if (ev.target === ev.currentTarget) onSurfacePointerDown(ev); }}
              >
                {order.map((id) => {
                  const widget = widgetsById.get(id);
                  const r = shown[id];
                  if (!widget || !r) return null;
                  return (
                    <FreeItem
                      key={id}
                      id={id}
                      widget={widget}
                      rect={r}
                      z={placements[id]?.z ?? 0}
                      locked={placements[id]?.locked === true}
                      proposed={unplaced.includes(id)}
                      selected={selection.includes(id)}
                      onPointerDown={onItemPointerDown}
                    />
                  );
                })}
              </div>
            </ThemeScope>

            <svg class="hv-fc__overlay" width={frame.width * scale} height={frame.height * scale} aria-hidden="true">
              {guides.map((gd, i) => gd.axis === 'v'
                ? <line key={i} class="hv-fc__guide" x1={gd.pos * scale} x2={gd.pos * scale} y1={gd.start * scale} y2={gd.end * scale} />
                : <line key={i} class="hv-fc__guide" y1={gd.pos * scale} y2={gd.pos * scale} x1={gd.start * scale} x2={gd.end * scale} />)}
              {sel.map((id) => {
                const r = shown[id];
                return (
                  <rect key={id}
                        class={`hv-fc__selection${multi && id === sel[0] ? ' hv-fc__selection--anchor' : ''}`}
                        x={r.x * scale + 0.5} y={r.y * scale + 0.5}
                        width={Math.max(0, r.w * scale - 1)} height={Math.max(0, r.h * scale - 1)} />
                );
              })}
              {groupBounds && (
                <rect class="hv-fc__groupBox" x={groupBounds.x * scale - 4} y={groupBounds.y * scale - 4}
                      width={groupBounds.w * scale + 8} height={groupBounds.h * scale + 8} />
              )}
              {marquee && (
                <rect class="hv-fc__marquee" x={marquee.x * scale} y={marquee.y * scale}
                      width={marquee.w * scale} height={marquee.h * scale} />
              )}
            </svg>

            {/* Griffe und Sperrmarken als Elemente ueber der Flaeche: sie
                bekommen Zeigerereignisse, die SVG-Ebene darunter nicht. */}
            {sel.map((id) => {
              const r = shown[id];
              const locked = placements[id]?.locked === true;
              return (
                <div key={id} class="hv-fc__frame"
                     style={{ left: `${r.x * scale}px`, top: `${r.y * scale}px`, width: `${r.w * scale}px`, height: `${r.h * scale}px` }}>
                  {locked && <span class="hv-fc__lockMark" title="Gesperrt"><Icon name="lock" /></span>}
                  {single === id && !locked && canEdit && HANDLES.map((h) => (
                    <span key={h} class={`hv-fc__handle hv-fc__handle--${h}`}
                          onPointerDown={(ev) => onHandlePointerDown(ev as unknown as PointerEvent, id, h)} />
                  ))}
                </div>
              );
            })}

            {badge && (
              <span class="hv-fc__badge" style={{
                left: `${(badge.rect.x + badge.rect.w / 2) * scale}px`,
                top: `${(badge.rect.y + badge.rect.h) * scale + 8}px`,
              }}>{badge.text}</span>
            )}
          </div>
        )}
      </div>

      <div class="hv-fc__status">
        {single && shown[single] && canEdit ? (
          <GeometryFields
            rect={shown[single]}
            z={placements[single]?.z ?? 0}
            locked={isLocked(single)}
            label={widgetsById.get(single)?.label || single}
            onChange={(field, v) => onGeometry(single, field, v)}
          />
        ) : multi ? (
          <span>
            {sel.length} ausgewählt. Bezug für Ausrichten und Angleichen ist das zuerst gewählte (kräftiger Rahmen).
          </span>
        ) : (
          <span class="hv-fc__hint">
            Klicken wählt, Umschalt oder Strg erweitert, auf leerer Fläche ziehen wählt mehrere. Pfeiltasten
            verschieben um 1 px, mit Umschalt um 10 px. Umschalt beim Ziehen hält die Richtung oder das
            Seitenverhältnis, Alt schaltet das Einrasten kurz ab.
          </span>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------- Bausteine

interface FreeItemProps {
  id: string;
  widget: WidgetDefinition;
  rect: Rect;
  z: number;
  locked: boolean;
  proposed: boolean;
  selected: boolean;
  onPointerDown(ev: PointerEvent, id: string): void;
}

function FreeItem({ id, widget, rect, z, locked, proposed, selected, onPointerDown }: FreeItemProps) {
  const cls = [
    'hv-free-item',
    'hv-hmi-cell',
    'hv-fc__item',
    BARE_KINDS.has(widget.kind) ? 'hv-free-item--bare' : '',
    locked ? 'hv-fc__item--locked' : '',
    proposed ? 'hv-fc__item--proposed' : '',
    selected ? 'hv-fc__item--selected' : '',
  ].filter(Boolean).join(' ');
  return (
    <div
      class={cls}
      data-widget-id={id}
      style={{
        left: `${rect.x}px`, top: `${rect.y}px`, width: `${rect.w}px`, height: `${rect.h}px`, zIndex: String(z),
      }}
      onPointerDown={(ev) => onPointerDown(ev as unknown as PointerEvent, id)}
    >
      <ItemContent widget={widget} />
    </div>
  );
}

/** Der Inhalt rendert nur neu, wenn sich das Widget aendert, nicht beim Ziehen.
 *  Ohne eigenen Wrapper: hmiCell.css und stage.css waehlen ueber das direkte
 *  Kind des Bausteins (`:has(> .hv-look)`), und die Flaeche soll genau so
 *  aussehen wie die Laufzeit. Bedienblind macht ihn FreeCanvas.css. */
const ItemContent = memo(function ItemContent({ widget }: { widget: WidgetDefinition }) {
  return <WidgetHost widget={widget} />;
});

// --------------------------------------------------------- Lagefelder

function GeometryFields(props: {
  rect: Rect;
  z: number;
  locked: boolean;
  label: string;
  onChange(field: keyof Rect, value: number): void;
}) {
  const fields: [keyof Rect, string][] = [['x', 'X'], ['y', 'Y'], ['w', 'B'], ['h', 'H']];
  return (
    <div class="hv-fc__geometry">
      <span class="hv-fc__geoLabel" title={props.label}>{props.label}</span>
      {fields.map(([field, label]) => (
        <label key={field} class="hv-fc__geoField">
          <span>{label}</span>
          <input
            type="number"
            step={1}
            value={props.rect[field]}
            disabled={props.locked && (field === 'x' || field === 'y')}
            onChange={(e) => props.onChange(field, Math.round(Number((e.currentTarget as HTMLInputElement).value)))}
          />
        </label>
      ))}
      <span class="hv-fc__geoMeta">Ebene {props.z}{props.locked ? ', gesperrt' : ''}</span>
    </div>
  );
}

// ------------------------------------------------------------ Hintergrund

function BackgroundMenu(props: {
  color: string | undefined;
  assetRef: string | undefined;
  onChange(next: { color?: string; assetRef?: string } | undefined, coalesceKey?: string): void;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  // The asset picker loads the asset list when it mounts; only while open.
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(props.color ?? '');
  useEffect(() => setHex(props.color ?? ''), [props.color]);
  useEffect(() => {
    const onPointer = (ev: PointerEvent) => {
      if (ref.current?.open && !ref.current.contains(ev.target as Node)) ref.current.open = false;
    };
    document.addEventListener('pointerdown', onPointer, true);
    return () => document.removeEventListener('pointerdown', onPointer, true);
  }, []);
  const set = (next: { color?: string; assetRef?: string }, key?: string) => {
    const merged = { color: props.color, assetRef: props.assetRef, ...next };
    props.onChange(merged.color || merged.assetRef ? merged : undefined, key);
  };

  return (
    <details class="hv-fc__bgMenu" ref={ref}
             onToggle={(e) => {
               const el = e.currentTarget as HTMLDetailsElement;
               setOpen(el.open);
               keepPanelInViewport(el);
             }}>
      <summary class="hv-btn hv-fc__bgToggle">
        <span class="hv-fc__bgSwatch" style={{ background: isHexColor(props.color) ? props.color : 'var(--surface-base)' }} />
        Hintergrund
      </summary>
      <div class="hv-fc__bgPanel">
        <div class="hv-fc__bgRow">
          <span class="hv-fc__bgLabel">Farbe</span>
          <input type="color" class="hv-fc__bgPicker" value={isHexColor(props.color) ? props.color : '#101114'}
                 aria-label="Hintergrundfarbe"
                 onInput={(e) => set({ color: (e.currentTarget as HTMLInputElement).value }, 'hintergrund:farbe')} />
          <input type="text" class="hv-fc__bgHex" value={hex} placeholder="aus dem Theme" maxLength={7}
                 spellcheck={false} aria-label="Hintergrundfarbe als #rrggbb"
                 onInput={(e) => {
                   const raw = (e.currentTarget as HTMLInputElement).value.trim();
                   setHex(raw);
                   if (raw === '') set({ color: undefined });
                   else if (isHexColor(raw)) set({ color: raw });
                 }} />
          <button type="button" class="hv-btn" disabled={!props.color} onClick={() => set({ color: undefined })}>
            Aus dem Theme
          </button>
        </div>
        <div class="hv-fc__bgRow hv-fc__bgRow--stack">
          <span class="hv-fc__bgLabel">
            Bild
            {props.assetRef && (
              <button type="button" class="hv-fc__bgLink" onClick={() => set({ assetRef: undefined })}>Bild entfernen</button>
            )}
          </span>
          {open && (
            <HmiAssetPicker value={props.assetRef ?? null} onChange={(id) => set({ assetRef: id ?? undefined })} />
          )}
          <span class="hv-fc__bgHint">Das Bild füllt die Fläche (zugeschnitten, mittig). Ohne Farbe gilt die des Themes.</span>
        </div>
      </div>
    </details>
  );
}

// ------------------------------------------------------------ Werkzeuge

function ToolGroup({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <span class="hv-fc__group" role="group" aria-label={label}>
      <span class="hv-fc__groupLabel">{label}</span>
      {children}
    </span>
  );
}

function Tool(props: { icon: IconName; title: string; disabled?: boolean; pressed?: boolean; onClick(): void }) {
  return (
    <button
      type="button"
      class={`hv-fc__tool${props.pressed ? ' is-pressed' : ''}`}
      title={props.title}
      aria-label={props.title}
      aria-pressed={props.pressed}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <Icon name={props.icon} />
    </button>
  );
}

type IconName =
  | 'alignLeft' | 'alignHCenter' | 'alignRight' | 'alignTop' | 'alignVCenter' | 'alignBottom'
  | 'distH' | 'distV' | 'sameW' | 'sameH' | 'front' | 'forward' | 'backward' | 'back'
  | 'lock' | 'unlock' | 'duplicate' | 'trash';

const ICONS: Readonly<Record<IconName, JSX.Element>> = {
  alignLeft: <><path d="M3 2v16" /><rect x="6" y="5" width="10" height="4" rx="1" /><rect x="6" y="11" width="6" height="4" rx="1" /></>,
  alignHCenter: <><path d="M10 2v16" /><rect x="4" y="5" width="12" height="4" rx="1" /><rect x="6" y="11" width="8" height="4" rx="1" /></>,
  alignRight: <><path d="M17 2v16" /><rect x="4" y="5" width="10" height="4" rx="1" /><rect x="8" y="11" width="6" height="4" rx="1" /></>,
  alignTop: <><path d="M2 3h16" /><rect x="5" y="6" width="4" height="10" rx="1" /><rect x="11" y="6" width="4" height="6" rx="1" /></>,
  alignVCenter: <><path d="M2 10h16" /><rect x="5" y="4" width="4" height="12" rx="1" /><rect x="11" y="6" width="4" height="8" rx="1" /></>,
  alignBottom: <><path d="M2 17h16" /><rect x="5" y="4" width="4" height="10" rx="1" /><rect x="11" y="8" width="4" height="6" rx="1" /></>,
  distH: <><path d="M2 3v14M18 3v14" /><rect x="7" y="6" width="6" height="8" rx="1" /></>,
  distV: <><path d="M3 2h14M3 18h14" /><rect x="6" y="7" width="8" height="6" rx="1" /></>,
  sameW: <><path d="M3 10h14M3 10l3-3M3 10l3 3M17 10l-3-3M17 10l-3 3" /></>,
  sameH: <><path d="M10 3v14M10 3l-3 3M10 3l3 3M10 17l-3-3M10 17l3-3" /></>,
  front: <><rect x="3" y="3" width="9" height="9" rx="1" class="hv-fc__iconMuted" /><rect x="8" y="8" width="9" height="9" rx="1" class="hv-fc__iconFill" /></>,
  forward: <><rect x="3" y="3" width="9" height="9" rx="1" class="hv-fc__iconMuted" /><rect x="8" y="8" width="9" height="9" rx="1" /></>,
  backward: <><rect x="8" y="8" width="9" height="9" rx="1" class="hv-fc__iconMuted" /><rect x="3" y="3" width="9" height="9" rx="1" /></>,
  back: <><rect x="8" y="8" width="9" height="9" rx="1" class="hv-fc__iconMuted" /><rect x="3" y="3" width="9" height="9" rx="1" class="hv-fc__iconFill" /></>,
  lock: <><rect x="4" y="9" width="12" height="8" rx="1.5" /><path d="M7 9V6.5a3 3 0 0 1 6 0V9" /></>,
  unlock: <><rect x="4" y="9" width="12" height="8" rx="1.5" /><path d="M7 9V6.5a3 3 0 0 1 5.8-1" /></>,
  duplicate: <><rect x="7" y="7" width="10" height="10" rx="1.5" /><path d="M13 4.5V4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h.5" /></>,
  trash: <><path d="M3 5h14M8 5V3h4v2M5 5l1 12h8l1-12" /></>,
};

function Icon({ name }: { name: IconName }) {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

// ------------------------------------------------------------- Helfer

/** Beim Ziehen an einem Griff am Rahmen abschneiden statt verschieben: die
 *  feste Kante soll stehen bleiben. */
function cutToFrame(r: Rect, frame: FrameSize): Rect {
  const x1 = Math.max(0, r.x);
  const y1 = Math.max(0, r.y);
  const x2 = Math.min(frame.width, r.x + r.w);
  const y2 = Math.min(frame.height, r.y + r.h);
  return { x: Math.round(x1), y: Math.round(y1), w: Math.round(Math.max(8, x2 - x1)), h: Math.round(Math.max(8, y2 - y1)) };
}

function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
